import { Control, Controller, useWatch } from "react-hook-form";

import {
  DateInput,
  ErrorMessage,
  Field,
  GoogleMapAutoComplete,
  Input,
  InputWithAddressAutoComplete,
  Label,
  PhoneInput,
  TextArea,
  validatePhone,
} from "@web-app/components";
import { GoogleAddress, OrderDeliveryType, SavedAddress } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import { getIsRequired } from "@web-app/utils/order";

import { FormValues } from "../../types";

interface PickupDetailsTabProps {
  control: Control<FormValues>;
  currentWorkspaceId: string;
  handleAddressSelect: (address: SavedAddress, isPickup: boolean) => void;
  isIndividualDelivery: boolean;
  onAddressSelect: (address: GoogleAddress | null, isPickup: boolean) => void;
  onSavePickupDetailsChange: (value: boolean) => void;
  pickupLocation: GoogleAddress | null;
  savePickupDetails: boolean;
}

export function PickupDetailsTab({
  control,
  currentWorkspaceId,
  handleAddressSelect,
  isIndividualDelivery,
  onAddressSelect,
  onSavePickupDetailsChange,
  pickupLocation,
  savePickupDetails,
}: PickupDetailsTabProps) {
  // Watch the timezone field to get its current value
  const timezone = useWatch({
    control,
    name: "timezone",
  }) as keyof typeof timeZoneMap | undefined;

  return (
    <div className="space-y-3 md:space-y-4 pb-10 md:pb-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label required>Full Name</Label>
          <Controller
            name="pickupName"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "pickupName",
              })
                ? "Full name is required"
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <InputWithAddressAutoComplete
                  type="text"
                  placeholder="Enter full name"
                  currentWorkspaceId={currentWorkspaceId}
                  onClickAddress={(address: { name: string }) => {
                    field.onChange(address.name);
                  }}
                  onAddressSelect={async (address) => {
                    await handleAddressSelect(address, true);
                  }}
                  {...field}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        <Field>
          <Label required>Phone Number</Label>
          <Controller
            name="pickupPhone"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "pickupPhone",
              })
                ? "Phone number is required"
                : false,
              validate: (value) => {
                if (!validatePhone(value)) {
                  return "Phone number must be in E.164 format (e.g., +1234567890)";
                }
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <PhoneInput
                  {...field}
                  onChange={(value: string) => {
                    field.onChange(value);
                  }}
                  defaultValue={field.value ? field.value : ""}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
      <Field>
        <Label required>Address</Label>
        <Controller
          name="pickupLocation"
          control={control}
          rules={{
            required: getIsRequired({
              orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
              propertyName: "pickupLocation",
            })
              ? "Pickup location is required"
              : false,
          }}
          render={({ fieldState: { error }, field: { onChange } }) => (
            <>
              <GoogleMapAutoComplete
                address={pickupLocation}
                setAddress={(address) => {
                  onAddressSelect(address, true);
                  onChange(address);
                }}
                placeholder="Enter pickup location"
                required={getIsRequired({
                  orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                  propertyName: "pickupLocation",
                })}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label required>
            {isIndividualDelivery
              ? "Pickup At (Start Time)"
              : "Pickup After (Date/Time)"}
          </Label>
          <Controller
            name="scheduledStartDateTime"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: isIndividualDelivery
                  ? "scheduledStartDateTime"
                  : "pickupStartTime",
              })
                ? "Start time is required"
                : false,
            }}
            render={({
              field: { onChange, ...rest },
              fieldState: { error },
            }) => (
              <>
                <DateInput
                  minDate={new Date()}
                  {...{
                    onChange: (date) => {
                      onChange(date);
                    },
                    ...rest,
                    required: getIsRequired({
                      orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                      propertyName: isIndividualDelivery
                        ? "scheduledStartDateTime"
                        : "pickupStartTime",
                    }),
                  }}
                  invalid={!!error}
                  timezone={timezone}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        {!isIndividualDelivery && (
          <Field>
            <Label required>Pickup Before (Date/Time)</Label>
            <Controller
              name="pickupEndTime"
              control={control}
              rules={{
                required: getIsRequired({
                  orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                  propertyName: "pickupEndTime",
                })
                  ? "End time is required"
                  : false,
              }}
              render={({
                field: { onChange, ...rest },
                fieldState: { error },
              }) => (
                <>
                  <DateInput
                    minDate={new Date()}
                    {...{
                      onChange: (date) => {
                        onChange(date);
                      },
                      ...rest,
                      required: getIsRequired({
                        orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                        propertyName: "pickupEndTime",
                      }),
                    }}
                    invalid={!!error}
                    timezone={timezone}
                  />
                  {error && <ErrorMessage message={error.message} />}
                </>
              )}
            />
          </Field>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label>Business Name</Label>
          <Controller
            name="pickupBusinessName"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "pickupBusinessName",
              })
                ? "Business name is required"
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  type="text"
                  placeholder="Enter business name"
                  {...field}
                  required={getIsRequired({
                    orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                    propertyName: "pickupBusinessName",
                  })}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        <Field>
          <Label>Service Time (minutes)</Label>
          <Controller
            name="pickupServiceTime"
            control={control}
            rules={{
              min: {
                message: "Service time must be a positive number",
                value: 0,
              },
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "pickupServiceTime",
              })
                ? "Service time is required"
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  type="number"
                  placeholder="Enter service time"
                  {...field}
                  required={getIsRequired({
                    orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                    propertyName: "pickupServiceTime",
                  })}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
      <div className="space-y-2">
        <Label>Order Requirements</Label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-x-4 gap-y-1 sm:gap-y-2">
          <Controller
            name="pickupOrderRequirements_SIGNATURE"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Signature Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="pickupOrderRequirements_PHOTO"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Photo Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="pickupOrderRequirements_NOTES"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Notes Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="pickupOrderRequirements_ITEMCHECKLIST"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Item Checklist Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
        </div>
      </div>
      <Field>
        <Label>Note</Label>
        <Controller
          name="pickupNote"
          control={control}
          render={({ field }) => (
            <TextArea placeholder="Enter pickup note" rows={3} {...field} />
          )}
        />
      </Field>
      <Field>
        <Controller
          name="savePickupDetails"
          control={control}
          render={() => (
            <Input
              type="checkbox"
              rightLabel="Save details to Address Book"
              value={savePickupDetails.toString()}
              onChange={(value: string) => {
                const boolValue = value === "true";
                onSavePickupDetailsChange(boolValue);
              }}
            />
          )}
        />
      </Field>
    </div>
  );
}
